import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CalendarRange, TrendingDown, Loader2, ChevronDown } from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import { usePersistedState } from '@/hooks/usePersistedState';

const API = process.env.REACT_APP_BACKEND_URL;

const METRICS = [
  { key: 'commission', label: 'Commission', fmt: 'myr' },
  { key: 'traded_lot', label: 'Traded Lot', fmt: 'dec' },
  { key: 'active_client', label: 'Active Client', fmt: 'int' },
  { key: 'deposit', label: 'Deposit', fmt: 'usd' },
  { key: 'subs', label: 'Subscribers', fmt: 'int' },
  { key: 'leads', label: 'Leads', fmt: 'int' },
  { key: 'closed', label: 'Closed', fmt: 'int' },
  { key: 'spend', label: 'Ad Spend', fmt: 'myr' },
  { key: 'com_per_lot', label: 'Com/Lot', fmt: 'usd' },
  { key: 'avg_lot_client', label: 'Avg Lot/Client', fmt: 'dec' },
  { key: 'commission_client', label: 'Commission/Client', fmt: 'myr' },
];

const fmtVal = (v, fmt) => {
  const n = Number(v || 0);
  if (fmt === 'myr') return `RM ${n.toLocaleString('en-MY', { maximumFractionDigits: 0 })}`;
  if (fmt === 'usd') return `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  if (fmt === 'dec') return n.toFixed(2);
  return n.toLocaleString();
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ogo', 'Sep', 'Okt', 'Nov', 'Dis'];
const monthLabel = (p) => (p ? `${MONTH_NAMES[parseInt(p.slice(5, 7), 10) - 1]} ${p.slice(0, 4)}` : '');

function DailyTooltip({ active, payload, label, metric, monthName, prevMonthName }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const delta = (row.cur ?? 0) - (row.prev ?? 0);
  const pct = row.prev ? (delta / row.prev) * 100 : (row.cur ? 100 : 0);
  const up = delta >= 0;
  return (
    <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] px-3 py-2 shadow-2xl">
      <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1">Hari {label}</p>
      <p className="text-xs text-white font-semibold font-mono">{monthName}: {fmtVal(row.cur, metric.fmt)}</p>
      <p className="text-xs text-[#f59e0b] font-mono">{prevMonthName}: {fmtVal(row.prev, metric.fmt)}</p>
      <p className={`text-xs font-mono font-semibold mt-1 ${up ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
        {up ? '+' : ''}{fmtVal(delta, metric.fmt)} ({up ? '+' : ''}{pct.toFixed(1)}%)
      </p>
    </div>
  );
}

export default function WarRoomDailyComparison({ ibId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const P = `war-room-daily:${ibId || 'none'}`;
  const [metricKey, setMetricKey] = usePersistedState(`${P}:metric`, 'commission');
  const [mode, setMode] = usePersistedState(`${P}:mode`, 'daily'); // daily | cumulative

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const token = localStorage.getItem('token');
    axios
      .get(`${API}/api/war-room/${ibId}/daily-comparison`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { if (alive) setData(res.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ibId]);

  const metric = METRICS.find((m) => m.key === metricKey) || METRICS[0];
  const monthName = monthLabel(data?.month);
  const prevMonthName = monthLabel(data?.prev_month);

  const chartRows = useMemo(() => {
    if (!data?.days) return [];
    let cCur = 0;
    let cPrev = 0;
    return data.days
      .filter((d) => d.is_mtd)
      .map((d) => {
        const cur = Number(d.cur?.[metric.key] || 0);
        const prev = Number(d.prev?.[metric.key] || 0);
        cCur += cur;
        cPrev += prev;
        const isRatio = ['com_per_lot', 'avg_lot_client', 'commission_client'].includes(metric.key);
        return {
          day: d.day,
          cur: mode === 'cumulative' && !isRatio ? +cCur.toFixed(2) : cur,
          prev: mode === 'cumulative' && !isRatio ? +cPrev.toFixed(2) : prev,
        };
      });
  }, [data, metric.key, mode]);

  const topDrops = useMemo(() => {
    if (!data?.days) return [];
    return data.days
      .filter((d) => d.is_mtd)
      .map((d) => {
        const cur = Number(d.cur?.[metric.key] || 0);
        const prev = Number(d.prev?.[metric.key] || 0);
        return { day: d.day, cur, prev, delta: cur - prev, pct: prev ? ((cur - prev) / prev) * 100 : 0 };
      })
      .filter((d) => d.delta < 0)
      .sort((a, b) => a.delta - b.delta)
      .slice(0, 3);
  }, [data, metric.key]);

  const totals = useMemo(() => {
    const cur = chartRows.reduce((s, r) => s + r.cur, 0);
    const prev = chartRows.reduce((s, r) => s + r.prev, 0);
    if (mode === 'cumulative' && chartRows.length) {
      const last = chartRows[chartRows.length - 1];
      return { cur: last.cur, prev: last.prev };
    }
    return { cur, prev };
  }, [chartRows, mode]);

  const totalDelta = totals.cur - totals.prev;
  const totalPct = totals.prev ? (totalDelta / totals.prev) * 100 : (totals.cur ? 100 : 0);

  return (
    <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden" data-testid="wr-daily-comparison">
      <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden">
        <div className="relative flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
            <CalendarRange size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white font-heading">Daily MTD Comparison</h3>
            <p className="text-xs text-[hsl(var(--text-muted))]">
              {monthName} vs {prevMonthName} · hari 1–{data?.mtd_day || '…'} · cari hari mana mula drop
            </p>
          </div>
        </div>
      </div>

      {/* Metric chips + mode toggle */}
      <div className="px-4 sm:px-6 py-3 border-b border-[hsl(var(--border))] flex flex-wrap items-center gap-2">
        {METRICS.map((m) => (
          <button
            key={m.key}
            onClick={() => setMetricKey(m.key)}
            data-testid={`wr-daily-metric-${m.key}`}
            className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-colors ${
              metricKey === m.key
                ? 'border-[#10b981] text-[#10b981] bg-[#10b981]/10'
                : 'border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:text-white'
            }`}
          >
            {m.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1 p-0.5 rounded-lg border border-[hsl(var(--border))]">
          {[{ id: 'daily', label: 'Harian' }, { id: 'cumulative', label: 'Kumulatif' }].map((t) => (
            <button
              key={t.id}
              onClick={() => setMode(t.id)}
              data-testid={`wr-daily-mode-${t.id}`}
              className={`px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-colors ${
                mode === t.id ? 'bg-[#E63946] text-white' : 'text-[hsl(var(--text-muted))] hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {loading ? (
          <div className="h-72 flex items-center justify-center text-[hsl(var(--text-muted))]">
            <Loader2 size={18} className="animate-spin mr-2" /> Memuatkan data harian…
          </div>
        ) : !chartRows.length ? (
          <p className="h-72 flex items-center justify-center text-xs text-[hsl(var(--text-muted))]" data-testid="wr-daily-empty">
            Tiada data harian untuk {monthName}.
          </p>
        ) : (
          <>
            {/* MTD totals strip */}
            <div className="flex flex-wrap items-center gap-4 mb-4 text-xs" data-testid="wr-daily-totals">
              <span className="text-[hsl(var(--text-muted))] uppercase text-[10px] tracking-wider">{metric.label} MTD</span>
              <span className="font-mono font-semibold text-white">{monthName}: {fmtVal(totals.cur, metric.fmt)}</span>
              <span className="font-mono text-[#f59e0b]">{prevMonthName}: {fmtVal(totals.prev, metric.fmt)}</span>
              <span className={`font-mono font-semibold ${totalDelta >= 0 ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
                {totalDelta >= 0 ? '+' : ''}{fmtVal(totalDelta, metric.fmt)} ({totalDelta >= 0 ? '+' : ''}{totalPct.toFixed(1)}%)
              </span>
            </div>

            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 10, fill: 'hsl(var(--text-muted))' }} stroke="hsl(var(--border))" />
                  <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--text-muted))' }} stroke="hsl(var(--border))" width={64}
                    tickFormatter={(v) => (metric.fmt === 'myr' ? `RM${Math.round(v / (Math.abs(v) >= 1000 ? 1000 : 1))}${Math.abs(v) >= 1000 ? 'k' : ''}` : v)} />
                  <Tooltip content={<DailyTooltip metric={metric} monthName={monthName} prevMonthName={prevMonthName} />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="cur" name={monthName} fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={26}>
                    {chartRows.map((r) => (
                      <Cell key={r.day} fill={r.cur < r.prev ? '#ef4444' : '#10b981'} />
                    ))}
                  </Bar>
                  <Line type="monotone" dataKey="prev" name={prevMonthName} stroke="#f59e0b" strokeWidth={2}
                    strokeDasharray="5 4" dot={{ r: 2, fill: '#f59e0b' }} activeDot={{ r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Top 3 drops */}
            <div className="mt-5 pt-4 border-t border-[hsl(var(--border))]" data-testid="wr-daily-top-drops">
              <p className="text-[10px] uppercase font-bold tracking-wider text-[hsl(var(--text-muted))] mb-2">
                Top 3 hari drop paling besar ({metric.label})
              </p>
              {!topDrops.length ? (
                <p className="text-xs text-[#10b981]">Tiada hari yang lebih rendah daripada {prevMonthName}.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {topDrops.map((d) => (
                    <div key={d.day} className="p-3 rounded-lg border border-[#ef4444]/30 bg-[#ef4444]/5" data-testid={`wr-daily-drop-${d.day}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold text-white">Hari {d.day}</span>
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#ef4444]">
                          <TrendingDown size={10} /> {d.pct.toFixed(1)}%
                        </span>
                      </div>
                      <p className="text-[10px] text-[hsl(var(--text-muted))] font-mono">
                        {fmtVal(d.cur, metric.fmt)} <span className="opacity-60">vs</span> {fmtVal(d.prev, metric.fmt)}
                      </p>
                      <p className="text-[11px] font-mono font-semibold text-[#ef4444]">{fmtVal(d.delta, metric.fmt)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
